import { useState } from 'react';
import { SearchRequired } from '../../components/States';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import { useTranslation } from 'react-i18next';
import type { Rarity } from '@anidex/shared/economy';
import type { QuestDTO } from '@anidex/shared/types';
import { ProgressMeter } from '../../components/Badges';
import { FragmentIcon } from '../../components/Brand';
import { CheckIcon, SearchIcon, SparkIcon, TargetIcon } from '../../components/Icons';
import { Pagination } from '../../components/Pagination';
import { Sheet } from '../../components/Sheet';
import { EmptyState, Spinner } from '../../components/States';
import { useToasts } from '../../components/Toasts';
import { useListPage } from '../../hooks/useListPage';
import { questDescription, questTitle } from '../../i18n/catalog';
import { errorMessage } from '../../services/api';
import { rarityLabel, rewardParts } from '../../services/format';
import { useClaimQuest, useCollection, useRerollQuest } from '../../services/queries';
import { AchievementPrize } from './AchievementRow';

function FragmentPickSheet({
  quest,
  onClose,
}: {
  quest: QuestDTO;
  onClose: () => void;
}) {
  const { t } = useTranslation('play');
  const claim = useClaimQuest();
  const toasts = useToasts();
  const [search, setSearch] = useState('');
  const deferredSearch = useDebouncedSearch(search);
  const listPage = useListPage(deferredSearch);
  const collection = useCollection({ search: deferredSearch || undefined, sort: 'name', page: listPage.page });
  const cards = collection.data?.items ?? [];
  const title = questTitle(quest.event, quest.title);

  const choose = (characterId: number, name: string) =>
    claim.mutate(
      { questId: quest.id, characterId },
      {
        onSuccess: (result) => {
          toasts.push({
            tone: 'reward',
            title: t('quests.done', { title }),
            description: t('rewardFor', { parts: rewardParts(quest.reward).join(', '), name }),
          });
          toasts.announceRewards(result.rewards);
          onClose();
        },
        onError: (error) => toasts.push({ tone: 'error', title: t('rewardUnavailable'), description: errorMessage(error) }),
      },
    );

  return (
    <Sheet open onClose={onClose} title={t('cards.which')} showTitle>
      <p className="quest-pick__intro">{t('cards.place', { count: quest.reward.fragments ?? 0 })}</p>
      <label className="quest-pick__search">
        <SearchIcon size={18} />
        <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('cards.search')} aria-label={t('cards.search')} autoComplete="off" enterKeyHint="search" />
      </label>
      {!deferredSearch ? (
        <SearchRequired />
      ) : collection.isPending ? (
        <Spinner />
      ) : cards.length === 0 ? (
        <EmptyState title={t('cards.emptyTitle')}>{t('cards.emptyBody')}</EmptyState>
      ) : (
        <>
          <ul className="quest-pick">
            {cards.map((card) => (
              <li key={card.character.id}>
                <button type="button" disabled={claim.isPending} onClick={() => choose(card.character.id, card.character.name)}>
                  {card.character.imageMediumUrl && <img src={card.character.imageMediumUrl} alt="" width={36} height={48} />}
                  <span>
                    <strong>{card.character.name}</strong>
                    <span className={`rarity-${card.character.rarity as Rarity}`}>{rarityLabel(card.character.rarity)}</span>
                  </span>
                  <span className="quest-pick__fragments">
                    <FragmentIcon size={14} />
                    {card.ownership.fragments}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <Pagination
            page={listPage.page}
            hasPrev={listPage.page > 0}
            hasNext={Boolean(collection.data?.nextCursor)}
            busy={collection.isFetching}
            onPrev={listPage.goPrev}
            onNext={listPage.goNext}
          />
        </>
      )}
    </Sheet>
  );
}

const STATUS_ICONS = { progress: TargetIcon, ready: SparkIcon, claimed: CheckIcon } as const;

export function QuestRow({ quest, compact = false }: { quest: QuestDTO; compact?: boolean }) {
  const { t } = useTranslation('play');
  const claim = useClaimQuest();
  const reroll = useRerollQuest();
  const toasts = useToasts();
  const [picking, setPicking] = useState(false);
  const status = quest.claimed ? 'claimed' : quest.completed ? 'ready' : 'progress';
  const Icon = STATUS_ICONS[status];
  const needsChoice = Boolean(quest.reward.fragmentsChoice && quest.reward.fragments);
  const title = questTitle(quest.event, quest.title, quest.variant);
  const description = questDescription(quest.event, quest.target, quest.description, quest.variant);

  const onClaim = () => {
    if (needsChoice) {
      setPicking(true);
      return;
    }
    claim.mutate(
      { questId: quest.id },
      {
        onSuccess: (result) => {
          const name = result.quest.rewardCharacter?.name;
          const parts = rewardParts(quest.reward).join(', ');
          toasts.push({
            tone: 'reward',
            title: t('quests.done', { title }),
            description: name ? t('rewardNamed', { parts, name }) : parts,
          });
          toasts.announceRewards(result.rewards);
        },
        onError: (error) => toasts.push({ tone: 'error', title: t('rewardUnavailable'), description: errorMessage(error) }),
      },
    );
  };

  return (
    <li className={`quest quest--${status}${compact ? ' quest--compact' : ''}`}>
      <div className="quest__head">
        <span className="quest__icon" aria-hidden="true">
          <Icon size={22} />
        </span>
        <div className="quest__copy">
          <p className="quest__title">{title}</p>
          {!compact && <p className="quest__description">{description}</p>}
        </div>
      </div>
      <div className="quest__track">
        <ProgressMeter value={quest.progress} max={quest.target} label={t('progress', { title })} />
        <span className="quest__count">
          {quest.progress} / {quest.target}
        </span>
      </div>
      <div className="quest__foot">
        <AchievementPrize reward={quest.reward} characterName={quest.claimed ? quest.rewardCharacter?.name : null} />
        {quest.rerollable && status === 'progress' && (
          <button
            type="button"
            className="button button--small"
            onClick={() =>
              reroll.mutate(quest.id, {
                onError: (error) => toasts.push({ tone: 'error', title: t('quests.rerollFailed'), description: errorMessage(error) }),
              })
            }
            disabled={reroll.isPending}
          >
            {t('quests.reroll')}
          </button>
        )}
        {status === 'ready' && (
          <button type="button" className="button button--reward" onClick={onClaim} disabled={claim.isPending}>
            {t('claim')}
          </button>
        )}
        {status === 'claimed' && (
          <span className="quest__stamp">
            <CheckIcon size={14} />
            {t('claimed')}
          </span>
        )}
      </div>
      {picking && <FragmentPickSheet quest={quest} onClose={() => setPicking(false)} />}
    </li>
  );
}
